"use client";

import { useCommute } from "@/hooks/ai-hooks";
import { motion } from "framer-motion";
import { Car, Clock, Loader2, MapPin, Navigation } from "lucide-react";
import { useLocale } from "next-intl";
import { useRef } from "react";

export default function CommuteCalculator({
  latitude,
  longitude,
}: {
  latitude?: number;
  longitude?: number;
}) {
  const locale = useLocale();
  const isRtl = locale === "ar";
  const inputRef = useRef<HTMLInputElement>(null);
  const commute = useCommute();

  if (!latitude || !longitude) return null;

  const run = (dest: string) => {
    const destination = dest.trim();
    if (!destination || commute.isPending) return;
    commute.mutate({ fromLat: latitude, fromLng: longitude, destination });
  };

  return (
    <div className="mt-8 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm" dir={isRtl ? "rtl" : "ltr"}>
      <div className="flex items-center gap-3 mb-4">
        <div className="size-11 rounded-2xl bg-gradient-to-br from-[#0d3589] to-[#071f52] flex items-center justify-center text-white shrink-0">
          <Navigation size={20} />
        </div>
        <div>
          <h3 className="font-black text-slate-900 text-base">
            {isRtl ? "كم يبعد عن وجهتك؟" : "How far from your destination?"}
          </h3>
          <p className="text-xs text-slate-400">
            {isRtl
              ? "احسب المسافة ووقت القيادة من هذا العقار إلى عملك أو أي مكان"
              : "Calculate driving distance & time from this property to anywhere"}
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(inputRef.current?.value ?? "");
        }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <div className="relative flex-1">
          <MapPin
            size={18}
            className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${isRtl ? "right-3" : "left-3"}`}
          />
          <input
            ref={inputRef}
            placeholder={isRtl ? "مثال: مطار الملك خالد، أو حي العليا" : "e.g. King Khalid Airport, or Olaya district"}
            className={`w-full h-12 bg-slate-50 border border-slate-200 rounded-2xl text-sm text-slate-800 font-medium placeholder:text-slate-400 outline-none focus:border-[#0d3589] focus:bg-white transition-all ${isRtl ? "pr-10 pl-4" : "pl-10 pr-4"}`}
          />
        </div>
        <button
          type="submit"
          disabled={commute.isPending}
          className="h-12 px-6 shrink-0 rounded-2xl bg-[#0d3589] text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#0a2a6e] active:scale-95 transition-all disabled:opacity-60"
        >
          {commute.isPending ? <Loader2 size={16} className="animate-spin" /> : <Car size={16} />}
          {isRtl ? "احسب" : "Calculate"}
        </button>
      </form>

      {commute.isError && (
        <p className="text-sm text-red-600 mt-3">
          {(commute.error as any)?.response?.data?.error ||
            (isRtl ? "تعذّر حساب المسافة." : "Could not calculate distance.")}
        </p>
      )}

      {commute.data && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 grid grid-cols-2 gap-3"
        >
          <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 text-center">
            <Car className="mx-auto text-[#0d3589] mb-1" size={22} />
            <p className="text-2xl font-black text-slate-900">
              {commute.data.distanceKm}
              <span className="text-sm font-bold text-slate-400 ms-1">
                {isRtl ? "كم" : "km"}
              </span>
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {isRtl ? "مسافة القيادة" : "Driving distance"}
            </p>
          </div>
          <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 text-center">
            <Clock className="mx-auto text-amber-500 mb-1" size={22} />
            <p className="text-2xl font-black text-slate-900">
              {commute.data.durationMin}
              <span className="text-sm font-bold text-slate-400 ms-1">
                {isRtl ? "دقيقة" : "min"}
              </span>
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {isRtl ? "وقت الوصول تقريباً" : "Approx. travel time"}
            </p>
          </div>
          {commute.data.destinationLabel && (
            <p className="col-span-2 text-xs text-slate-400 text-center truncate">
              <MapPin size={11} className="inline -mt-0.5 me-1" />
              {commute.data.destinationLabel}
              {commute.data.approx && (
                <span className="ms-1 text-amber-500">
                  {isRtl ? "(تقديري)" : "(estimated)"}
                </span>
              )}
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
}
